import { formatDelta, formatValue, isNumber } from './piFormat.js';

const DAY_MS = 86400000;
const WEEKDAYS = ['dom', 'seg', 'ter', 'qua', 'qui', 'sex', 'sáb'];
const MAX_WEEKEND_POINTS = 120;
const MIN_WEEKEND_STEP = 3;
const MAX_DELTA_PCT = 1000;

const pad = (value) => (value < 10 ? '0' : '') + value;

export const parseDay = (date) => {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(typeof date === 'string' ? date : '');
  return match ? Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])) : null;
};

export const weekdayOf = (date) => {
  const time = parseDay(date);
  return time === null ? null : new Date(time).getUTCDay();
};

export const isWeekend = (date) => {
  const day = weekdayOf(date);
  return day === 0 || day === 6;
};

export const isDailySeries = (dates) => {
  if (!Array.isArray(dates) || dates.length < 2) return false;
  let smallest = null;
  for (let i = 1; i < dates.length; i++) {
    const previous = parseDay(dates[i - 1]);
    const current = parseDay(dates[i]);
    if (previous === null || current === null || current <= previous) return false;
    if (smallest === null || current - previous < smallest) smallest = current - previous;
  }
  return smallest === DAY_MS;
};

export const indexDaysBefore = (dates, index, days) => {
  const list = dates || [];
  const time = index >= 0 && index < list.length ? parseDay(list[index]) : null;
  if (time === null || !isNumber(days) || days < 1) return -1;
  const target = time - days * DAY_MS;
  for (let i = index - 1; i >= 0 && i >= index - days; i--) {
    if (parseDay(list[i]) === target) return i;
  }
  return -1;
};

export const dayTitle = (date) => {
  const time = parseDay(date);
  if (time === null) return typeof date === 'string' ? date : '';
  const day = new Date(time);
  return WEEKDAYS[day.getUTCDay()] + ', ' + pad(day.getUTCDate()) + '/' + pad(day.getUTCMonth() + 1) + '/' + day.getUTCFullYear();
};

export const nearestIndex = (chart, x) => {
  const columns = (chart && chart.columns) || [];
  if (!columns.length || !isNumber(x)) return -1;
  let best = 0;
  for (let i = 1; i < columns.length; i++) {
    if (Math.abs(columns[i].x - x) < Math.abs(columns[best].x - x)) best = i;
  }
  return best;
};

const valueAt = (points, index) => {
  if (!points || index < 0 || index >= points.length || !points[index]) return null;
  return isNumber(points[index].value) ? points[index].value : null;
};

export const deltaText = (value, base, unit) => {
  if (!isNumber(value) || !isNumber(base)) return '';
  const deltaAbs = value - base;
  const pct = unit !== 'pct' && base > 0 ? (deltaAbs / base) * 100 : null;
  const deltaPct = pct !== null && Math.abs(pct) < MAX_DELTA_PCT ? pct : null;
  return formatDelta(deltaAbs, deltaPct, unit);
};

export const deltaTone = (value, base, higherIsBetter) => {
  if (!isNumber(value) || !isNumber(base) || value === base) return 'flat';
  return (value > base) === (higherIsBetter !== false) ? 'good' : 'bad';
};

export const isBeforeCoverage = (date, coverageFrom) => {
  const day = parseDay(date);
  const from = parseDay(coverageFrom);
  return day !== null && from !== null && day < from;
};

export const isWindowIncomplete = (date, coverageFrom, size) => {
  const day = parseDay(date);
  const from = parseDay(coverageFrom);
  const length = isNumber(size) && size > 1 ? size : 1;
  return length > 1 && day !== null && from !== null && day >= from && day - (length - 1) * DAY_MS < from;
};

export const hoverModel = (chart, index, options) => {
  const settings = options || {};
  const dates = (chart && chart.dates) || [];
  if (!dates.length || !isNumber(index) || index < 0 || index >= dates.length) return null;
  const date = dates[index];
  const unit = settings.unit || 'count';
  const before = isBeforeCoverage(date, settings.coverageFrom);
  const daily = settings.daily === undefined ? isDailySeries(dates) : settings.daily === true;
  const previousIndex = daily ? indexDaysBefore(dates, index, 1) : index - 1;
  const weekIndex = daily ? indexDaysBefore(dates, index, 7) : -1;
  const comparable = (at) => at >= 0 && !before && !isBeforeCoverage(dates[at], settings.coverageFrom);
  const format = settings.formatValue || ((value) => formatValue(value, unit));
  const rows = (chart.series || []).map((item) => {
    const point = item.points ? item.points[index] : null;
    const value = point && isNumber(point.value) ? point.value : null;
    const size = item.window || 1;
    const incomplete = !before && isWindowIncomplete(date, settings.coverageFrom, size);
    const usable = (at) => comparable(at) && !incomplete && !isWindowIncomplete(dates[at], settings.coverageFrom, size);
    const previous = usable(previousIndex) ? valueAt(item.points, previousIndex) : null;
    const lastWeek = usable(weekIndex) ? valueAt(item.points, weekIndex) : null;
    return {
      key: item.key,
      label: item.label,
      color: item.color,
      dash: item.dash,
      y: point ? point.y : null,
      value: value,
      text: value === null ? 'sem valor' : format(value),
      partial: !!(point && point.partial),
      muted: before || incomplete,
      incomplete: incomplete,
      delta: deltaText(value, previous, unit),
      deltaTone: deltaTone(value, previous, item.higherIsBetter),
      weekDelta: deltaText(value, lastWeek, unit),
      weekDeltaTone: deltaTone(value, lastWeek, item.higherIsBetter),
    };
  });
  const flags = [];
  if (before) flags.push({ kind: 'coverage', text: 'Antes do início da medição: o valor não representa o uso real' });
  else if (rows.some((row) => row.partial)) {
    flags.push({ kind: 'partial', text: settings.partialText || (daily ? 'Dia parcial: o número ainda pode mudar' : 'Período parcial: o número ainda pode mudar') });
  }
  const incompleteRows = rows.filter((row) => row.incomplete);
  if (incompleteRows.length) {
    flags.push({ kind: 'window', text: 'Janela incompleta (' + incompleteRows.map((row) => row.label).join(', ') + '): começa antes do início da medição, valor subestimado' });
  }
  if (daily && !before && isWeekend(date)) flags.push({ kind: 'weekend', text: 'Fim de semana: queda costuma ser sazonal' });
  const extras = !before && typeof settings.extras === 'function' ? (settings.extras(date, rows) || []).filter((extra) => extra && extra.text) : [];
  const column = chart.columns ? chart.columns[index] : null;
  return {
    index: index,
    date: date,
    x: column ? column.x : null,
    title: daily ? dayTitle(date) : (settings.formatDate ? settings.formatDate(date) : date),
    previousLabel: daily ? 'vs dia anterior' : 'vs ponto anterior',
    weekLabel: daily ? 'vs mesmo dia da semana anterior' : '',
    hasWeek: daily && rows.some((row) => row.weekDelta),
    rows: rows,
    flags: flags,
    extras: extras,
    beforeCoverage: before,
  };
};

export const hoverSummary = (model) => {
  if (!model) return '';
  const parts = [model.title];
  model.rows.forEach((row) => parts.push(row.label + ': ' + row.text + (row.delta ? ' (' + model.previousLabel + ' ' + row.delta + ')' : '')));
  model.extras.forEach((extra) => parts.push(extra.label + ': ' + extra.text));
  model.flags.forEach((flag) => parts.push(flag.text));
  return parts.join('. ');
};

const step = (chart) => {
  const columns = (chart && chart.columns) || [];
  if (columns.length < 2) return chart && chart.plot ? chart.plot.right - chart.plot.left : 0;
  return (columns[columns.length - 1].x - columns[0].x) / (columns.length - 1);
};

const clampBand = (chart, left, right) => {
  const from = Math.max(chart.plot.left, left);
  const to = Math.min(chart.plot.right, right);
  return to > from ? { x: from, width: to - from } : null;
};

export const coverageBand = (chart, coverageFrom) => {
  const dates = (chart && chart.dates) || [];
  if (!dates.length || parseDay(coverageFrom) === null) return null;
  let last = -1;
  for (let i = 0; i < dates.length; i++) {
    if (isBeforeCoverage(dates[i], coverageFrom)) last = i;
  }
  if (last === -1) return null;
  const right = last === dates.length - 1 ? chart.plot.right : (chart.columns[last].x + chart.columns[last + 1].x) / 2;
  const band = clampBand(chart, chart.plot.left, right);
  return band ? Object.assign(band, { label: 'antes da medição' }) : null;
};

export const weekendBands = (chart, coverageFrom) => {
  const dates = (chart && chart.dates) || [];
  if (!isDailySeries(dates) || dates.length > MAX_WEEKEND_POINTS) return [];
  const half = step(chart) / 2;
  if (half * 2 < MIN_WEEKEND_STEP) return [];
  const coverage = coverageBand(chart, coverageFrom);
  const floor = coverage ? coverage.x + coverage.width : chart.plot.left;
  const bands = [];
  const close = (first, last) => {
    const band = clampBand(chart, Math.max(floor, chart.columns[first].x - half), chart.columns[last].x + half);
    if (band) bands.push(band);
  };
  let start = -1;
  dates.forEach((date, i) => {
    const weekend = isWeekend(date);
    const joined = start !== -1 && weekend && parseDay(date) - parseDay(dates[i - 1]) === DAY_MS;
    if (start !== -1 && !joined) {
      close(start, i - 1);
      start = -1;
    }
    if (weekend && start === -1) start = i;
  });
  if (start !== -1) close(start, dates.length - 1);
  return bands;
};

export const tooltipPlacement = (anchor, tipWidth, boxWidth, gap) => {
  if (!isNumber(anchor) || !isNumber(tipWidth) || !isNumber(boxWidth) || boxWidth <= 0) return { left: 0, below: false };
  const space = isNumber(gap) ? gap : 12;
  if (anchor + space + tipWidth <= boxWidth) return { left: anchor + space, below: false };
  if (anchor - space - tipWidth >= 0) return { left: anchor - space - tipWidth, below: false };
  return { left: Math.max(0, Math.min(anchor - tipWidth / 2, boxWidth - tipWidth)), below: true };
};



// WEBPACK FOOTER //
// ./src/vue/pages/components/pi/piChartHover.js